import type { IBatchStore } from './batch-store-types';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import logger from '../../utils/logger';
import { reaction } from 'mobx';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { beltSwitch } from './device-switches/belt-switch';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import {
  getBatchOrder,
  getProportionTwoOrder,
} from './material-substitution/proportion-schedule';
import {
  waterShouldWaitForMoistureProbe,
  updateAggTarget,
  updateMixDesign,
  updateWaterTarget,
} from './live-moisture';
import { DischargeAmountType } from '../../containers/Discharge/DischargeAmountType';
import { BeltStartMode } from '../../components/DeviceAsDom/Belt';
import { ToleranceMode } from '../../constants/Configuration';
import { autoCutOffAdjust } from './auto-cut-off-adjust';
import { addFeedLog } from '../../containers/Batch/DataToBatch';
import { FeedOperation } from '../../constants/FeedOperation';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { getSecondaryBinFreeFallForAutoTuning } from './free-fall-prediction/secondary-bin-freefall';
import {
  FeedTracker,
  keysToEnhancedSnakeCase,
} from './feed-tracker/feed-tracker';
import {
  mlConfigData,
  getPredictionFromModel,
  mlConfigUrls,
  mlModelUrls,
  mlSessionCache,
} from './free-fall-prediction/free-fall-predition-from-model';
import { PERMISSION } from '../../modules/newAuth/model/User';
import { DeviceTypes } from '../../constants/DeviceModels';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type { DischargeScale } from '../../../types/api/Discharge';
import type { IDevice } from '../../../types/device/DeviceModels';
import { isRunModeNewYork } from './run-mode';

const FEATURE_AUTOMOISTURE_FOR_METERED = isFeatureEnabled('automoisture_for_metered');
const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('live_moisture_for_weighed');
const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

export const feedModeMixin = {

  feedMode(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    mft: number,
  ): void {
    const { material, scale } = data;
    const { configuration } = this.settingsStore;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const mData = runningData ? runningData.scales[scale.id].find((v: IWeighedDataToBatch) => v.material.itemId === material.itemId) : null;
    const binStatus = this.getDeviceStatus(primaryBin?.id);
    const hasBeenSubstituted = data.isSubstituted || binStatus?.isSubstituted;
    const weight = this.getScaleWeight(scale);

    //check if material feed finish
    if (!runningData || (mData?.isFeedFinish)) {
      logger.warn(material.code, 'feed already finish!!');
      return;
    }

    logger.log('FEED mode', material.code, mft, data.mft, binStatus.status);

    if (!primaryBin) {
      this.showError({ key: 'No primary bin for material', props: { material: material.code }});
      return;
    }

    if (this.isAbort || !this.isActiveDrop(runningData)) {
      return;
    }

    if (this.isFinishFeedDevice(primaryBin.id)) {
      this.timers.clear(timerKey.device(scale.id));

      this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== primaryBin.id);
      this.unHoldFeedDevice(primaryBin.id);

      this.deviceStatus.set(primaryBin.id, {
        ...this.getDeviceStatus(primaryBin.id),
        status: DeviceStatus.SETTLING,
      });

      this.getScaleWeightAfterSettlingTime(scale, materialDetails.scaleSettlingTime, (weight) => {
        const b1s = this.getDeviceStatus(primaryBin.id);
        this.deviceStatus.set(primaryBin.id, {
          ...b1s,
          weight: this.getBinWeightForStatus(b1s, weight),
          status: DeviceStatus.FINISH,
        });
        if (secondaryBin) {
          const b2s = this.getDeviceStatus(secondaryBin.id);
          this.deviceStatus.set(secondaryBin.id, {
            ...b2s,
            weight: this.getBinWeightForStatus(b2s, weight),
            status: DeviceStatus.FINISH,
          });
        }

        const index = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);

        this.setFeedFinish(data, runningData, index);

        this.updateInventory(data, weight, runningData, true);
        this.batchDataForScale(scale.id, runningData);
      });

      return;
    }

    if (
      binStatus.status === DeviceStatus.WAIT_MOIST
      && (
        this.materialStore.isWater(material.itemId)
        || (
          this.materialStore.isSecondWater(material.itemId)
          && toNumber(runningData.secondWater) === 100
        )
      )
      && waterShouldWaitForMoistureProbe(runningData, this)
    ) {
      this.timers.clear(timerKey.device(scale.id));
      this.timers.set(timerKey.device(scale.id), () => this.feedMode(data, runningData, mft), 2000, `Feed mode - scale #${scale.id}`);
      return;
    }

    // Postpone feed if the device is on hold
    if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(primaryBin.id)) {
      this.timers.set(timerKey.device(scale.id), () => this.feedMode(data, runningData, mft), 2000, `Feed mode - scale #${scale.id}`);
      return;
    }

    if (mData.overcapacityTarget) {
      const primaryScale = runningData.sequence?.discharges?.find((v: DischargeScale) => v.isPrimary);
      const washAmountFactor =
        primaryScale?.washAmountType === DischargeAmountType.Percent && primaryScale?.washAmount
          ? primaryScale.washAmount / 100
          : 0.2; // 20% of wash water
      const minWashWater = mData.overcapacityTarget < scale.capacity * washAmountFactor
        ? data.mft * washAmountFactor
        : mData.overcapacityTarget;

      if (!mData.isFeedFinishHeadWater) {
        mft = data.mft - minWashWater;
        runningData.headWater = mft;
        runningData.washWater = 0;
      } else if (mData.isDischargeFinishHeadWater && !mData.isFeedFinishWashWater) {
        mft = minWashWater;
        runningData.headWater = 0;
        runningData.washWater = mft;
        this.deviceStatus.set(primaryBin.id, {
          ...binStatus,
          prevWeight: -1 * binStatus.weight || 0,//-1* - adding weight from previous batch
        });
      } else {
        logger.log('feed finish both head & wash water over capacity target', material.code, mft);
        return;
      }

      logger.log('change water over capacity, feed mode', material.code, washAmountFactor, minWashWater,
        mft, mData.overcapacityTarget, data.mft, runningData.headWater, runningData.washWater, binStatus);
    }

    const mftPrimary = mft - this.getFreeFall(primaryBin, initialData);
    const isJogModeSize = weight >= mftPrimary;

    // case when user hold feed and might add more material manually, we have to check jog mode size
    if (isJogModeSize) {
      logger.log('isJogModeSize', isJogModeSize, material.code, weight, mft);
      this.jogMode(data, initialData, mft);
      return;
    }

    const lastData = initialData.scales[scale.id][initialData.scales[scale.id].length - 1];
    const isLastMaterialOnTheScale = lastData?.material.itemId === data.material.itemId;
    logger.log(scale.description, 'is feeding last material?', material.code, isLastMaterialOnTheScale);

    if (scale.beltId && isLastMaterialOnTheScale) {
      const belt = this.devicesStore.getDeviceById(scale.beltId);
      if (belt?.startMode === BeltStartMode.WeightingLastMaterial) {
        logger.log(scale.description, 'starting belt, feeding last material for scale');
        beltSwitch(this.rootStore, data.scale.beltId);
      }
    }

    let mftSecondary = secondaryBin ? mft - this.getFreeFall(secondaryBin, initialData) : 0;

    if (FEATURE_AUTO_TUNING && primaryBin.autoTuning && secondaryBin) {
      const secondaryFreeFallForAutoTuning = getSecondaryBinFreeFallForAutoTuning(materialDetails.type);
      mftSecondary = mftPrimary > secondaryFreeFallForAutoTuning
        ? mftPrimary - secondaryFreeFallForAutoTuning
        : 0;
    }
    const unit = materialDetails.itemBatchUnitSymbol || this.getUnitById(scale.unitId);

    // keep data up to date when bin swap
    if (primaryBin.id !== data.bin.id) {
      const oldPrimary = data.bin;
      const oldPrimaryStatus = this.getDeviceStatus(oldPrimary.id);

      data = {
        ...data,
        bin: primaryBin,
        material: {
          ...material,
        },
      };
      const index = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
      runningData.scales[scale.id][index] = data;

      if (secondaryBin && this.isHoldFeedDevice(secondaryBin.id)) {
        this.unHoldFeedDevice(secondaryBin.id);
      }

      //case when user switch the bins in material settings not on the ui
      if (
        this.isHoldFeedDevice(oldPrimary.id)
        && oldPrimaryStatus.status === DeviceStatus.HOLD
        && oldPrimaryStatus.uid === initialData.uid
        && oldPrimaryStatus.currentDrop === initialData.currentDrop
        && binStatus.status === DeviceStatus.IDLE
        && binStatus.uid !== initialData.uid
      ) {
        const materialBin = this.devicesStore.getBinFromMaterial(material, oldPrimary.id);
        if (materialBin && !materialBin.isActive) {
          this.deviceStatus.set(primaryBin.id, {
            ...oldPrimaryStatus,
            id: primaryBin.id,
          });

          this.deviceStatus.set(oldPrimary.id, this.getDeviceStatusDefault(oldPrimary));

          this.unHoldFeedDevice(oldPrimary.id);
          this.holdFeedDevice(primaryBin.id);

          logger.log('bin switched', oldPrimary.id, primaryBin.id, material.code);

          this.timers.set(timerKey.device(scale.id), () => this.feedMode(data, runningData, mft), 2000, `Feed mode - scale #${scale.id}`);
          return;
        }

      }
    }

    const materialBin = secondaryBin ? this.devicesStore.getBinFromMaterial(material, secondaryBin.id) : null;
    if (materialBin && !materialBin.isActive && secondaryBin) {
      this.deviceStatus.set(secondaryBin.id, this.getDeviceStatusDefault(secondaryBin));
    }

    aeratorSwitch(this.rootStore, primaryBin.aeratorId);

    const b1s = this.getDeviceStatus(primaryBin.id);
    let primaryTarget = isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative
      ? mft
      : configuration.onlyJogOnLastDrop && runningData.currentDrop < runningData.allDrops
        ?  mftPrimary - b1s.prevWeight
        : toNumber(data.mft);

    this.deviceStatus.set(primaryBin.id, {
      ...b1s,
      unit,
      target: primaryTarget,
      targetIsNet: hasBeenSubstituted,
    });

    if (secondaryBin) {
      aeratorSwitch(this.rootStore, secondaryBin.aeratorId);
      let secondaryFreeFall = this.getFreeFall(secondaryBin, runningData);
      if (FEATURE_AUTO_TUNING && primaryBin.autoTuning) {
        const secondaryFreeFallForAutoTuning = getSecondaryBinFreeFallForAutoTuning(materialDetails.type);
        if (primaryTarget > secondaryFreeFallForAutoTuning) {
          secondaryFreeFall = secondaryFreeFallForAutoTuning;
        }
      }
      const secondaryTarget = toNumber(
        (isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative ? mft : data.mft)
        - secondaryFreeFall,
      );
      this.deviceStatus.set(secondaryBin.id, {
        ...this.getDeviceStatus(secondaryBin.id),
        unit,
        target: Math.max(secondaryTarget, 0),
        targetIsNet: hasBeenSubstituted,
      });
    }

    this.deviceStatus.set(scale.id, {
      ...this.getDeviceStatus(scale.id),
      weight,
      unit,
      target: configuration.onlyJogOnLastDrop && runningData.currentDrop < runningData.allDrops ? mftPrimary : mft,
      status: DeviceStatus.FEED,
    });

    if (secondaryBin) {
      const delayDiff = Math.abs(secondaryBin.delay - primaryBin.delay) * 1000;
      let firstBin, secondBin, firstMft, secondMft;

      // Choose which bin opens first based on delay
      if (secondaryBin.delay > primaryBin.delay) {
        // Primary opens first
        firstBin = primaryBin;
        secondBin = secondaryBin;
        firstMft = mftPrimary;
        secondMft = mftSecondary;
      } else {
        // Secondary opens first
        firstBin = secondaryBin;
        secondBin = primaryBin;
        firstMft = mftSecondary;
        secondMft = mftPrimary;
      }

      // Open first bin
      if (firstMft < secondMft) {
        // Smaller mft opens in first position to minimize risk of overshooting
        this.setWeight('+', scale.inputPNo, firstMft, 0, firstBin.gateOnePNo, 0);
      } else {
        this.setWeight('+', scale.inputPNo, 0, firstMft, 0, firstBin.gateOnePNo);
      }
      this.deviceStatus.set(firstBin.id, {
        ...this.getDeviceStatus(firstBin.id),
        status: DeviceStatus.FEED,
      });

      // Schedule second bin opening
      this.timers.set(timerKey.device(scale.id), () => {
        const firstBinStatus = this.getDeviceStatus(firstBin.id);
        if (firstBinStatus.status !== DeviceStatus.FEED) {
          return;
        }

        // Open second bin
        if (firstMft < secondMft) {
          this.setWeight('+', scale.inputPNo, firstMft, secondMft, firstBin.gateOnePNo, secondBin.gateOnePNo);
        } else {
          this.setWeight('+', scale.inputPNo, secondMft, firstMft, secondBin.gateOnePNo, firstBin.gateOnePNo);
        }
        this.deviceStatus.set(secondBin.id, {
          ...this.getDeviceStatus(secondBin.id),
          status: DeviceStatus.FEED,
        });
      }, delayDiff, `Schedule second bin opening - scale #${scale.id}`);
    } else {
      // Only primary bin
      this.setWeight('+', scale.inputPNo, 0, mftPrimary, 0, primaryBin.gateOnePNo);
      this.deviceStatus.set(primaryBin.id, {
        ...b1s,
        status: DeviceStatus.FEED,
      });
    }

    data = {
      ...data,
      startWeight: weight,
    };

    addFeedLog({
      batchTicketId: runningData.id,
      operationName: FeedOperation.OPEN_FREE_FALL,
      scaleWeight: weight,
      ...this.rootStore.weatherStore,
    }, data);

    this.checkWeightStatus(scale.inputPNo, data, 0, runningData, () => {
      if (this.isActiveDrop(runningData)) {
        const binStatus = this.getDeviceStatus(primaryBin.id);

        // rare case when bin is paused at 70% because of moisture but we got scale finish signal
        if (binStatus.status === DeviceStatus.WAIT_MOIST) {
          logger.log('weight status finish but water wait for moisture', binStatus.status, data?.material.code);
          return;
        }

        logger.log('scale weight status on finish', scale.id, 'reset-weight');
        this.resetWeight(scale.inputPNo);

        if (this.isFinishFeedDevice(primaryBin.id) || (configuration.onlyJogOnLastDrop && runningData.currentDrop < runningData.allDrops)) {
          this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== primaryBin.id);
          this.unHoldFeedDevice(primaryBin.id);

          if (primaryBin && !initialData.isWashout) {
            aeratorSwitch(this.rootStore, primaryBin.aeratorId, false);
            screwSwitch(this.rootStore, primaryBin.screwId, false);
            beltSwitch(this.rootStore, primaryBin.beltId, false);
            vibratorSwitch(this.rootStore, primaryBin.vibratorId, false);
          }

          this.deviceStatus.set(primaryBin.id, {
            ...this.getDeviceStatus(primaryBin.id),
            status: DeviceStatus.DELAY,
          });

          this.timers.set(timerKey.device(primaryBin.id), () => {

            const b1s = this.getDeviceStatus(primaryBin.id);
            this.deviceStatus.set(primaryBin.id, {
              ...b1s,
              status: DeviceStatus.SETTLING,
            });

            this.getScaleWeightAfterSettlingTime(scale, materialDetails.scaleSettlingTime, (weight) => {
              const b1s = this.getDeviceStatus(primaryBin.id);

              const index = initialData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
              this.setFeedFinish(data, initialData, index);

              this.updateInventory(data, weight, initialData, this.isFinishFeedDevice(primaryBin.id));

              this.deviceStatus.set(primaryBin.id, {
                ...b1s,
                weight: this.getBinWeightForStatus(b1s, weight),
                status: DeviceStatus.FINISH,
              });

              addFeedLog({
                batchTicketId: runningData.id,
                operationName: FeedOperation.SETTLING,
                duration: materialDetails.scaleSettlingTime,
                scaleWeight: weight,
                recommendation: mftPrimary -  data.startWeight,
                ...this.rootStore.weatherStore,
              }, data);

              logger.table(data.feedLogs);

              this.batchDataForScale(scale.id, initialData);
            });

          }, (materialDetails.freeFallCloseDelay || 0) * 1000, `Free fall close delay - bin #${primaryBin.id}`);

        } else {
          this.deviceStatus.set(primaryBin.id, {
            ...this.getDeviceStatus(primaryBin.id),
            status: DeviceStatus.DELAY,
          });

          this.timers.set(timerKey.device(primaryBin.id), () => {

            addFeedLog({
              batchTicketId: runningData.id,
              operationName: FeedOperation.CLOSE_FREE_FALL,
              duration: materialDetails.freeFallCloseDelay,
              scaleWeight: this.getScaleWeight(scale),
              recommendation: mftPrimary - data.startWeight,
              ...this.rootStore.weatherStore,
            }, data);

            autoCutOffAdjust(this, primaryBin, data, initialData);

            if (
              materialDetails.liveMoisture
              && (FEATURE_AUTOMOISTURE_FOR_METERED || FEATURE_LIVE_MOISTURE_FOR_WEIGHED)
            ) {
              this.adjustWaterTarget(data, initialData, mft);
            } else {

              const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
              const mData = runningData
                ? runningData.scales[scale.id]
                  .find((v: IWeighedDataToBatch) => v.material.itemId === material.itemId)
                : data;

              // rare case when target might be adjusted by live moisture and mft is not accurate
              if (mData.mft === data.mft) {
                this.jogMode(data, initialData, mft);
              } else {
                const diff = mData.mft - data.mft;
                logger.log('updating mft:', material.code, mData.mft, data.mft, diff);
                this.jogMode(mData, runningData, mft + diff);
              }
            }
          }, (materialDetails.freeFallCloseDelay || 0) * 1000, `Close free fall - bin #${primaryBin.id}`);
        }

        if (secondaryBin) {
          aeratorSwitch(this.rootStore, secondaryBin.aeratorId, false);

          const b2 = this.getDeviceStatus(secondaryBin.id);
          if (b2.status !== DeviceStatus.FINISH) {
            this.deviceStatus.set(secondaryBin.id, {
              ...b2,
              weight: this.getBinWeightForStatus(b2, this.getScaleWeight(scale)),
              status: DeviceStatus.FINISH,
            });
          }
        }

      }
    }, () => {
      if (secondaryBin) {
        aeratorSwitch(this.rootStore, secondaryBin.aeratorId, false);

        const b2 = this.getDeviceStatus(secondaryBin.id);
        this.deviceStatus.set(secondaryBin.id, {
          ...b2,
          weight: this.getBinWeightForStatus(b2, this.getScaleWeight(scale)),
          status: DeviceStatus.FINISH,
        });
      }
    });

    this.checkFeedWeight(data, initialData, mftPrimary);
    this.checkFeedMetrics(data, initialData, mft, mft - mftPrimary);
  },

  checkFeedMetrics(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    target: number,
    cutoff: number,
  ) {
    if (
      data?.isTargetAdjusted
      || this.isAbort
      || !this.isActiveDrop(initialData)
    ) {
      return;
    }

    const { material, materialDetails, scale } = data;
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    if (!primaryBin?.dynamicFreeFall) {
      logger.log('bin not allowed to check feed metrics', material.code, primaryBin?.id);
      return;
    }

    const secondaryBinFreeFall = secondaryBin ? getSecondaryBinFreeFallForAutoTuning(materialDetails.type) : 0;
    const secondaryBinCutOff = secondaryBinFreeFall && target > secondaryBinFreeFall
      ? target - secondaryBinFreeFall
      : 0;

    let lastFreeFallPrediction: null|number = null;

    const aerator = this.devicesStore.getDeviceById(primaryBin.aeratorId);
    const vibrator = this.devicesStore.getDeviceById(primaryBin.vibratorId);
    const dustCollector = this.devicesStore.devices.find((v: IDevice) => v?.deviceTypeName === DeviceTypes.DustCollector);
    const inventory = materialDetails.deviceBalance?.find((v: any) => v.deviceId === primaryBin.id)?.currentBalance || 0;

    const feedTracker = new FeedTracker({
      storage: this.newAuthStore?.storage,
      binId: primaryBin.id,
      plantId: this.plantId,
      materialType: materialDetails.type,
      materialName: material.code,
      target,
      cutoff,
      inventory,
    });

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (
          this.isAbort
          || !this.isActiveDrop(initialData)
          || this.isHoldFeed
          || initialData.isHoldLoad
          || this.isHoldFeedDevice(primaryBin.id)
        ) {
          reaction.dispose();
          return;
        }

        const binStatus = this.getDeviceStatus(primaryBin.id);
        const scaleWeight = this.getScaleWeight(scale);
        let freeFallPredictionUsed = false;
        let predictionDelay = 0;

        feedTracker.addData({
          weight: scaleWeight,
          binStatus: binStatus.status,
          aeratorPNoStatus: Number(this.isOutputOn(aerator?.switchPNo)),
          binGatePNoStatus: Number(this.isOutputOn(primaryBin?.gateOnePNo)),
          scalePNoStatus: Number(this.weightsStatus[scale.inputPNo - 1]),
          vibratorPNoStatus: Number(this.isOutputOn(vibrator?.switchOnePNo)),
          dustCollectorPNoStatus: Number(this.isOutputOn(dustCollector?.switchPNo)),
        });

        const feedMetrics = feedTracker.getCalculatedFeedData();
        const feedMetricToSnakeCase = keysToEnhancedSnakeCase(feedMetrics);
        if (!this.feedMetrics) {
          this.feedMetrics = {};
        }

        if (
          binStatus.status === DeviceStatus.FEED
          && this.newAuthStore.user?.hasAccess(PERMISSION.BATCH_AI_2)
          && mlModelUrls.has(primaryBin.id)
          && mlConfigUrls.has(primaryBin.id)
          && mlConfigData.has(primaryBin.id)
          && mlSessionCache.has(String(mlModelUrls.get(primaryBin.id)))
        ) {

          if (!primaryBin.dynamicFreeFallStart || !primaryBin.dynamicFreeFallEnd) {
            logger.warn('using default dynamic free fall start/end', primaryBin.id, material.code);
          }

          const dynamicFreeFallStart = primaryBin.dynamicFreeFallStart || 1000;
          const dynamicFreeFallEnd = primaryBin.dynamicFreeFallEnd || 300;
          const timeStart = Date.now();

          (async () => {
            try {
              const mlConfig: any = mlConfigData.get(primaryBin.id);
              const freeFallPrediction = await getPredictionFromModel(primaryBin.id, feedMetricToSnakeCase);
              const remainingTarget = target - scaleWeight;
              predictionDelay = Date.now() - timeStart;

              if (
                freeFallPrediction > dynamicFreeFallEnd
                && remainingTarget < dynamicFreeFallStart
                && remainingTarget > dynamicFreeFallEnd
              ) {
                const mftPrimary = target - freeFallPrediction;
                logger.log(material.code, primaryBin.id, 'free fall prediction:', freeFallPrediction,
                  'weight:', scaleWeight, 'target:', target, mftPrimary, remainingTarget,
                  'secondary bin:', secondaryBin?.id, secondaryBinFreeFall, secondaryBinCutOff,
                  'prediction delay:', predictionDelay, 'ms');

                if (freeFallPrediction !== lastFreeFallPrediction) {
                  this.setWeight('+', scale.inputPNo, secondaryBinCutOff, mftPrimary, 0, primaryBin.gateOnePNo);
                  freeFallPredictionUsed = true;
                  lastFreeFallPrediction = freeFallPrediction;
                  logger.log(material.code, primaryBin.id, 'using free fall prediction', freeFallPrediction);
                }
              }

              this.feedMetrics[primaryBin.id] = {
                ...feedMetricToSnakeCase,
                free_fall_prediction: freeFallPrediction,
                free_fall_prediction_used: freeFallPredictionUsed,
                free_fall_prediction_delay: predictionDelay,
                model: mlConfig?.model,
              };
            } catch(e: any) {
              logger.error(e?.message ?? String(e));
              this.feedMetrics[primaryBin.id] = {
                ...feedMetricToSnakeCase,
                free_fall_prediction_log: `${e?.message ?? String(e)}`,
              };
            }
          })();

        } else {
          logger.debug('feed metrics saved', primaryBin.id, material.code);

          this.feedMetrics[primaryBin.id] = {
            ...feedMetricToSnakeCase,
            free_fall_prediction_log: binStatus.status === DeviceStatus.FEED ? `
              permission: ${this.newAuthStore.user?.hasAccess(PERMISSION.BATCH_AI_2)},
              configUrl: ${mlConfigUrls.get(primaryBin.id)},
              modelUrl: ${mlModelUrls.get(primaryBin.id)},
              mlConfigDownloaded: ${mlConfigData.has(primaryBin.id)},
              mlModelDownloaded: ${mlSessionCache.has(String(mlModelUrls.get(primaryBin.id)))},
              ` : '',
          };
        }

        if (
          binStatus.status === DeviceStatus.FINISH
          || this.isAbort
          || !this.isActiveDrop(initialData)
        ) {
          reaction.dispose();
          this.setTimeout(() => {
            feedTracker.finish();
            logger.log(this.feedMetrics[primaryBin.id]);
          }, 300);
          this.setTimeout(() => {
            this.feedMetrics[primaryBin.id] = null;
          }, 600);
        }

        logger.debug(JSON.stringify(feedTracker.feedData));

      });
  },

  adjustWaterTarget(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    mft: number,
  ): void {
    const runningData: IBatchData = this.running.find((v: IBatchData) => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const { constituent, scale, materialDetails, bin, isTargetAdjusted } = data;

    if (!isTargetAdjusted && constituent) {
      const aggTarget = toNumber(this.getTargetForWeighed(constituent, runningData, materialDetails.autoMoistureAmount));
      mft = updateAggTarget(this,  { runningData, data, mft, aggTarget });
      updateWaterTarget(this, { runningData, data, aggTarget });
      updateMixDesign(runningData, data, aggTarget, this);

      if (!waterShouldWaitForMoistureProbe(runningData, this)) {
        this.onBatchAction(runningData);
        this.restartHeadWaterPump(runningData);
      }
    }

    const freeFall = this.getFreeFall(bin, runningData);
    const weight = this.getScaleWeight(scale);
    const isJogModeSize = mft - weight <= toNumber(freeFall || 0);

    if (isJogModeSize || isTargetAdjusted) {
      this.jogMode(data, runningData, mft);
    } else {
      this.feedMode( { ...data, isTargetAdjusted: true }, runningData, mft);
    }
  },

  setFeedFinish(this: IBatchStore, data: IWeighedDataToBatch, runningData: IBatchData, scaleIndex: number): void {
    const { scale, material, proportionTwoTarget } = data;
    const scaleData = runningData?.scales?.[scale.id];
    const entry = scaleData?.[scaleIndex];

    if (!scaleData || !entry) {
      logger.warn('setFeedFinish: missing scale entry', { scaleId: scale?.id, scaleIndex, materialCode: material?.code });
      return;
    }

    if (proportionTwoTarget) {
      if (entry.isFeedFinishProportionOne) {
        entry.isFeedFinishProportionTwo = true;
        entry.isFeedFinish = true;
      } else {
        entry.isFeedFinishProportionOne = true;
        const nextScaleData = scaleData[scaleIndex +1];

        if (nextScaleData) {
          nextScaleData.wasPrevDataMovedWithProportion = true;

          logger.log('change order for batch proportion two', material.code, proportionTwoTarget);
          // A substitute that took over a swapped-out material's schedule uses that material's order slots.
          const orderOf = (v: IWeighedDataToBatch): number => (v.isFeedFinishProportionOne
            ? getProportionTwoOrder(v)
            : getBatchOrder(v) || 1) as number;
          scaleData.sort((a: IWeighedDataToBatch, b: IWeighedDataToBatch) => orderOf(a) - orderOf(b));
        }
      }
    } else {
      entry.isFeedFinish = true;
    }
  },

};
